<template>
  <v-dialog v-if="isFetched" v-model="isVisibleComputed" persistent max-width="900px">
    <PrintedContainerBasic
      v-if="printOptions.printingType === 'basic'"
      :clustered-features="clusteredFeatures"
      :printed-features-properties="printedFeaturesProperties"
      :printed-layer-name="printedLayerName"
    />
    <PrintedContainerCadastral
      v-if="printOptions.printingType === 'cadastral'"
      :scale="$refs.printingMap.scale"
    />
    <v-card>
      <v-card-title class="pb-5">
        <span class="headline">{{ $i18n.t('dialog.header.printing') }}</span>
      </v-card-title>
      <v-card-text>
        <v-row no-gutters>
          <v-col cols="3" class="pr-2">
            <MapPrintingOptions
              ref="mapPrintingOptions"
              v-model="printOptions"
              :is-valid.sync="isPritntingOptionsValid"
            />
          </v-col>
          <v-col cols="9" class="pl-2">
            <PritntingMap
              ref="printingMap"
              :parent-map="parentMap"
              :loading="loading"
              :clustered-features.sync="clusteredFeatures"
              :printed-features-properties.sync="printedFeaturesProperties"
              :printing-type="printOptions.printingType"
              @drawBoxZoom="drawBoxZoom = $event"
            />
            <div class="map--invisible" id="map--invisible" ref="printer" />
            <v-row no-gutters class="error--text" v-if="drawBoxZoom <= 15">
              <v-col
                cols="12"
                style="text-align: center"
              >{{$i18n.t('dialog.print.accuracyWarning')}}</v-col>
            </v-row>
          </v-col>
        </v-row>
      </v-card-text>
      <v-card-actions>
        <div class="flex-grow-1" />
        <v-btn
          color="primary"
          text
          @click="close"
          :disabled="loading"
        >{{ $i18n.t('dialog.print.no') }}</v-btn>
        <v-btn
          color="primary"
          text
          @click="print"
          :disabled="!isPritntingOptionsValid"
          :loading="loading"
        >{{ $i18n.t('dialog.print.yes') }}</v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>
<script>
import Map from 'ol/Map';
import View from 'ol/View';
import html2canvas from 'html2canvas';

import vuetify from '@/plugins/vuetify.js';

const vuetifyColors = vuetify.preset.theme.themes.light;

import pdfMake from 'pdfmake/build/pdfmake';
import pdfFonts from 'pdfmake/build/vfs_fonts';
pdfMake.vfs = pdfFonts.pdfMake.vfs;

const PDFJS = require('pdfjs-dist/es5/build/pdf.js');
const PdfjsWorker = require('worker-loader!pdfjs-dist/es5/build/pdf.worker.js');
PDFJS.GlobalWorkerOptions.workerPort = new PdfjsWorker();
export default {
  components: {
    MapPrintingOptions: () => import('@/components/printing/MapPrintingOptions'),
    PritntingMap: () => import('@/components/printing/PritntingMap'),
    PrintedContainerBasic: () => import('@/components/printing/PrintedContainerBasic'),
    PrintedContainerCadastral: () => import('@/components/printing/PrintedContainerCadastral')
  },
  props: {
    isVisible: {
      type: Boolean,
      default: () => {
        return false;
      }
    },
    parentMap: {
      required: true,
      validator: value => {
        return value === undefined || value instanceof Map;
      }
    },
    printedLayerName: {
      type: String,
      required: true
    }
  },
  computed: {
    isVisibleComputed: {
      get() {
        return this.isVisible;
      },
      set(nV) {
        this.$emit('update:isVisible', nV);
      }
    },
    paperSize() {
      if (!this.printOptions.paperFormat) {
        return [0, 0];
      }
      const paperSize = [...this.paperSeries[this.printOptions.paperFormat]];
      if (this.printOptions.orientation === 'landscape') {
        paperSize.reverse();
      }
      return paperSize;
    }
  },
  data: () => ({
    clusteredFeatures: [],
    drawBoxZoom: 20,
    isFetched: false,
    isPritntingOptionsValid: false,
    loading: false,
    paperSeries: {
      A0: [0.814, 1.189],
      A1: [0.594, 0.841],
      A2: [0.42, 0.594],
      A3: [0.297, 0.42],
      A4: [0.21, 0.297]
    },
    printedFeaturesProperties: {
      builtUp: {
        features: [],
        color_name: 'accent',
        color: vuetifyColors.accent
      },
      notBuiltUp: {
        features: [],
        color_name: 'accent',
        color: vuetifyColors.accent
      },
      development: {
        features: [],
        color_name: 'accent',
        color: vuetifyColors.accent
      }
    },
    printOptions: {}
  }),
  methods: {
    close() {
      this.$refs.mapPrintingOptions.$refs.form.resetValidation();
      this.$refs.printingMap
        .getLayerByName('printExtent')
        .getSource()
        .clear();
      this.$refs.printingMap.getInteractionByName('printExtentDragInteraction').setActive(false);
      this.isVisibleComputed = false;
    },
    getFilteredLayers() {
      return this.parentMap
        .getLayers()
        .getArray()
        .filter(l => {
          return (
            l.get('name') !== 'printExtent' &&
            l.get('name') !== this.printedLayerName &&
            (l.type !== 'TILE' || l.get('group') === 'baselayers')
          );
        });
    },
    print() {
      this.loading = true;
      document.body.style.cursor = 'progress';
      const layers = this.$refs.printingMap.getPrintedLayers(this.printedLayerName);
      this.preparePrintingMap(layers);
    },
    preparePrintingMap(layers) {
      const [width, height] = this.paperSize.map(size => {
        return (size * 1000 * parseInt(this.printOptions.resolution)) / 25.4;
      });
      const marginsFactor = 6 - this.printOptions.paperFormat.substring(1, 2);
      const parentMapLayers = this.getFilteredLayers();
      const map = new Map({
        layers: [...parentMapLayers, ...layers],
        target: 'map--invisible',
        view: new View()
      });
      map.once('rendercomplete', async event => {
        const { canvas } = event.context;
        await this.$nextTick();
        const gridCanvas = await html2canvas(document.getElementById('printedContainer'), {
          backgroundColor: 'white'
        });
        const gridURI = gridCanvas.toDataURL('image/png');
        const getLayout = (padding, border = true) => {
          const color = this.printOptions.printingType === 'basic' ? vuetifyColors.primary : '#000000';
          const width = border ? 2 : 0;
          return {
            vLineWidth: (i, node) => {
              return width;
            },
            hLineWidth: (i, node) => {
              return width;
            },
            hLineColor: (i, node) => {
              return color;
            },
            vLineColor: (i, node) => {
              return color;
            },
            paddingLeft: (i, node) => {
              return padding;
            },
            paddingRight: (i, node) => {
              return padding;
            },
            paddingTop: (i, node) => {
              return padding;
            },
            paddingBottom: (i, node) => {
              return padding;
            }
          };
        };
        const firstColumnHeight = height - 55 * marginsFactor + (marginsFactor - 2) * 4;
        const columns = [
          {
            style: 'map',
            width: (width / 3) * (this.printOptions.printingType === 'basic' ? 2 : 3) - 20 * marginsFactor,
            layout: getLayout(0, true),
            table: {
              heights:
                (height - 55 * marginsFactor) *
                (this.printOptions.printingType === 'basic'
                  ? 1
                  : this.printOptions.orientation === 'portrait'
                  ? 0.9
                  : 0.8),
              body: [
                [
                  {
                    image: canvas.toDataURL('image/jpeg'),
                    fit: [
                      (width / 3) * (this.printOptions.printingType === 'basic' ? 2 : 3) - 20 * marginsFactor,
                      (height - 50 * marginsFactor) *
                        (this.printOptions.printingType === 'basic'
                          ? 1
                          : this.printOptions.orientation === 'portrait'
                          ? 0.9
                          : 0.8)
                    ]
                  }
                ]
              ]
            }
          }
        ];
        if (this.printOptions.printingType === 'basic') {
          columns.unshift({
            width: width / 3 - 20 * marginsFactor,
            style: 'table',
            layout: getLayout(5, true),
            table: {
              heights: firstColumnHeight,
              body: [
                [
                  {
                    image: gridURI,
                    fit: [width / 3 - 30 * marginsFactor, height - 55 * marginsFactor]
                  }
                ]
              ]
            }
          });
        }
        const content = [{ columns }];
        if (this.printOptions.printingType === 'cadastral') {
          content.unshift({
            style: 'table',
            width: width - 20 * marginsFactor,
            layout: getLayout(0, false),
            table: {
              heights: (height - 55 * marginsFactor) * (this.printOptions.orientation === 'portrait' ? 0.1 : 0.2),
              body: [
                [
                  {
                    image: gridURI,
                    // text: 'CBRE',
                    fit: [
                      width - 40 * marginsFactor,
                      (height - 55 * marginsFactor) * (this.printOptions.orientation === 'portrait' ? 0.1 : 0.2)
                    ]
                  }
                ]
              ]
            }
          });
        }
        const docDefinition = {
          pageSize: { width: parseInt(width), height: parseInt(height) },
          pageOrientation: this.printOptions.orientation,
          pageMargins: 20 * marginsFactor,
          content
        };
        const pdfFile = pdfMake.createPdf(docDefinition);
        if (this.printOptions.fileFormat === 'PDF') {
          pdfFile.download('wydruk.pdf');
        } else {
          pdfFile.getBase64(base => {
            this.prepareCanvas(base);
          });
        }
        document.body.style.cursor = 'auto';
        this.printedFeaturesProperties = {
          builtUp: {
            features: [],
            color_name: 'accent',
            color: vuetifyColors.accent
          },
          notBuiltUp: {
            features: [],
            color_name: 'accent',
            color: vuetifyColors.accent
          },
          development: {
            features: [],
            color_name: 'accent',
            color: vuetifyColors.accent
          }
        };
        this.clusteredFeatures = [];
        this.loading = false;
      });
      map.setSize([
        (width / 3) * (this.printOptions.printingType === 'basic' ? 2 : 3) -
          (this.printOptions.printingType === 'basic' ? 0 : 7 * marginsFactor),
        height * (this.printOptions.orientation === 'portrait' ? 0.9 : 0.8)
      ]);
      map.getView().fit(this.$refs.printingMap.getPaperExtent(), {
        size: [
          (width / 3) * (this.printOptions.printingType === 'basic' ? 2 : 3) -
            (this.printOptions.printingType === 'basic' ? 0 : 7 * marginsFactor),
          height * (this.printOptions.orientation === 'portrait' ? 0.9 : 0.8)
        ],
        constrainResolution: true
      });
    },
    prepareCanvas(base) {
      const loadingTask = PDFJS.getDocument({ data: atob(base) });
      loadingTask.promise.then(
        pdf => {
          pdf.getPage(1).then(page => {
            const viewport = page.getViewport({ scale: 1 });

            const canvas = document.getElementById('the-canvas');
            const context = canvas.getContext('2d');
            canvas.height = viewport.height;
            canvas.width = viewport.width;

            const renderContext = {
              canvasContext: context,
              viewport: viewport
            };
            const renderTask = page.render(renderContext);
            renderTask.promise.then(() => {
              this.downloadCanvas(canvas);
            });
          });
        },
        reason => {
          this.$root.$emit('showSnackbar', reason);
        }
      );
    },
    downloadCanvas(canvas) {
      const tag = document.createElement('a');
      tag.href = canvas.toDataURL(`image/${this.printOptions.fileFormat.toLowerCase()}`);
      tag.download = `wydruk.${this.printOptions.fileFormat.toLowerCase()}`;
      document.body.appendChild(tag);
      tag.click();
      document.body.removeChild(tag);
    }
  },
  watch: {
    'printOptions.printingType'(nV) {
      if (nV && this.printOptions.orientation && this.printOptions.paperFormat) {
        this.$nextTick(() => {
          if (this.$refs.printingMap) {
            this.$refs.printingMap.addExtentToMap(this.paperSize);
          }
        });
      }
    },
    'printOptions.orientation'(nV) {
      if (nV && this.printOptions.paperFormat && this.printOptions.printingType) {
        this.$nextTick(() => {
          if (this.$refs.printingMap) {
            this.$refs.printingMap.addExtentToMap(this.paperSize);
          }
        });
      }
    },
    'printOptions.paperFormat'(nV) {
      if (nV && this.printOptions.orientation && this.printOptions.printingType) {
        this.$nextTick(() => {
          if (this.$refs.printingMap) {
            this.$refs.printingMap.addExtentToMap(this.paperSize);
          }
        });
      }
    },
    isVisibleComputed(nV) {
      if (nV) {
        this.isFetched = true;
        if (this.$refs.printingMap) {
          this.$refs.printingMap.scale = undefined;
        }
        this.printOptions = {};
        this.drawBoxZoom = 20;
      } else {
        this.printOptions = {};
        this.drawBoxZoom = 20;
      }
    }
  }
};
</script>
<style lang="scss" scoped>
.map--invisible {
  display: none;
  height: 200px;
  width: 200px;
}
</style>